import { formatTradeDateOnlyForDisplay } from "./displayDateTime";
import type { Report } from "../types/reports";
import { getEvidenceDocumentRoot } from "./reportEvidencePointer";
import { getReportTypeDisplayLabel } from "./reportTypeRules";

const TIMEZONE_LIKE_KEYS = [
  "timezone",
  "reporting_timezone",
  "time_zone",
  "timeZone",
  "display_timezone",
  "user_timezone",
] as const;

function pickNonEmptyTimezone(...candidates: unknown[]): string | null {
  for (const c of candidates) {
    if (typeof c === "string") {
      const t = c.trim();
      if (t) return t;
    }
  }
  return null;
}

function asRecord(o: unknown): Record<string, unknown> | null {
  return o && typeof o === "object" && !Array.isArray(o) ? (o as Record<string, unknown>) : null;
}

/** `scope_inputs` / policy-sim blobs often carry `timezone` next to local calendar fields. */
function timezoneFromScopeLike(obj: unknown): string | null {
  const r = asRecord(obj);
  if (!r) return null;
  return pickNonEmptyTimezone(r.timezone, r.reporting_timezone);
}

/**
 * Breadth-first search for a string timezone on nested objects (API shapes vary).
 * Skips huge `trades` / `findings` arrays and prioritizes small meta objects.
 */
function findTimezoneDeep(root: unknown, maxVisits: number): string | null {
  if (root == null || typeof root !== "object") return null;
  const queue: unknown[] = [root];
  const seen = new Set<unknown>();
  let visits = 0;
  while (queue.length > 0 && visits < maxVisits) {
    const node = queue.shift();
    visits += 1;
    if (node == null || typeof node !== "object") continue;
    if (seen.has(node)) continue;
    seen.add(node);

    if (Array.isArray(node)) {
      if (node.length > 250) continue;
      for (const el of node) {
        if (el && typeof el === "object" && !seen.has(el)) queue.push(el);
      }
      continue;
    }

    const o = node as Record<string, unknown>;
    for (const key of TIMEZONE_LIKE_KEYS) {
      if (Object.prototype.hasOwnProperty.call(o, key)) {
        const found = pickNonEmptyTimezone(o[key]);
        if (found) return found;
      }
    }

    const childPriority = (key: string): number => {
      if (key === "meta" || key === "scope_inputs" || key === "request" || key === "window" || key === "period") return 0;
      if (key === "policy_tree" || key === "policy" || key === "root" || key === "view_state") return 1;
      return 2;
    };

    const children: Array<[string, unknown]> = [];
    for (const [k, v] of Object.entries(o)) {
      if (v == null || typeof v !== "object") continue;
      if (k === "trades" && Array.isArray(v)) continue;
      if (k === "findings" && Array.isArray(v)) continue;
      if (Array.isArray(v) && v.length > 250) continue;
      children.push([k, v]);
    }
    children.sort(([ka], [kb]) => {
      const d = childPriority(ka) - childPriority(kb);
      if (d !== 0) return d;
      return ka.localeCompare(kb);
    });
    for (const [, v] of children) {
      if (!seen.has(v)) queue.push(v);
    }
  }
  return null;
}

/**
 * Timezone in effect when the report was run, for display (IANA id when available).
 * Reads report fields and common `input_snapshot` / bundle meta shapes.
 */
export function getReportRunTimezoneForDisplay(report: Report | null | undefined): string | null {
  if (!report) return null;
  const rep = report as unknown as Record<string, unknown>;
  const snap = report.input_snapshot;
  const snapRec = asRecord(snap);
  const snapMeta = asRecord(snapRec?.meta);
  const snapRequest = asRecord(snapRec?.request);
  const docRoot = getEvidenceDocumentRoot(snap);
  const docRec = asRecord(docRoot);
  const bundleMeta = asRecord(docRec?.meta);
  const scopeOnReport = asRecord(rep.scope_inputs);
  const scopeOnSnap = asRecord(snapRec?.scope_inputs);

  const direct = pickNonEmptyTimezone(
    report.reporting_timezone,
    report.timezone,
    rep.timezone,
    rep.reporting_timezone,
    scopeOnReport?.timezone,
    scopeOnReport?.reporting_timezone,
    snapRec?.timezone,
    snapRec?.reporting_timezone,
    scopeOnSnap?.timezone,
    scopeOnSnap?.reporting_timezone,
    snapRequest?.timezone,
    snapRequest?.reporting_timezone,
    snapMeta?.timezone,
    snapMeta?.reporting_timezone,
    bundleMeta?.timezone,
    bundleMeta?.reporting_timezone,
    timezoneFromScopeLike(rep.policy_sim_config),
    timezoneFromScopeLike(rep.simulator_config),
    timezoneFromScopeLike(rep.trade_policy_sim_config)
  );
  if (direct) return direct;

  const repEntries = report as unknown as Record<string, unknown>;
  for (const [k, v] of Object.entries(repEntries)) {
    if (k === "input_snapshot" || k === "findings" || k === "narrative") continue;
    if (v == null || typeof v !== "object") continue;
    const t = findTimezoneDeep(v, 80);
    if (t) return t;
  }

  const fromSnap = findTimezoneDeep(snap, 800);
  if (fromSnap) return fromSnap;

  return findTimezoneDeep(docRoot, 400);
}

export type ReportWindowFields = {
  timeframe?: string | null;
  start_date?: string | null;
  end_date?: string | null;
};

export function humanizeDashboardTimeframe(tf: string): string {
  const t = tf.trim().toLowerCase().replace(/_/g, "-");
  if (t === "all-time" || t === "alltime") return "All time";
  if (t === "today") return "Today";
  if (t === "yesterday") return "Yesterday";
  if (t === "this-week" || t === "thisweek") return "This week";
  if (t === "last-week" || t === "lastweek") return "Last week";
  if (t === "this-month" || t === "thismonth") return "This month";
  if (t === "last-month" || t === "lastmonth") return "Last month";
  const m = /^last-(\d+)-days?$/.exec(t);
  if (m) return `Last ${m[1]} days`;
  return tf
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

export function formatIanaTimezoneWithUtcOffset(timeZoneId: string, referenceDate: Date = new Date()): string {
  const trimmed = timeZoneId.trim();
  if (!trimmed) return trimmed;
  if (/^(UTC|GMT)\s*[+\-−]/i.test(trimmed)) return trimmed;
  if (/^[+\-−]\d{1,2}(:\d{2})?$/.test(trimmed)) return trimmed;

  try {
    const dtf = new Intl.DateTimeFormat("en-US", {
      timeZone: trimmed,
      timeZoneName: "longOffset",
    });
    const parts = dtf.formatToParts(referenceDate);
    const offsetLabel = parts.find((p) => p.type === "timeZoneName")?.value;
    if (offsetLabel) {
      const utcStyle = offsetLabel.replace(/^GMT/i, "UTC");
      return `${trimmed} (${utcStyle})`;
    }
  } catch {
    // Invalid or unsupported time zone id for this environment.
  }
  return trimmed;
}

/**
 * Human-readable report analysis window for list/detail UI.
 * When `whenUnset` is "omit", returns null if there are no dates and no timeframe (detail page hides chip).
 */
export function formatReportWindowLabelForDisplay(
  report: ReportWindowFields,
  options?: { timeZone?: string; whenUnset?: "all-time" | "omit" }
): string | null {
  const timeZone = options?.timeZone;
  const whenUnset = options?.whenUnset ?? "all-time";

  if (report.start_date && report.end_date) {
    const start = formatTradeDateOnlyForDisplay(report.start_date, timeZone);
    const end = formatTradeDateOnlyForDisplay(report.end_date, timeZone);
    if (start === "—" || end === "—") return "Custom range";
    if (start === end) return start;
    return `${start} – ${end}`;
  }
  if (report.timeframe) return humanizeDashboardTimeframe(report.timeframe);
  return whenUnset === "omit" ? null : "All time";
}

type ReportTitleFields = Pick<Report, "title" | "report_type" | "report_version" | "timeframe" | "start_date" | "end_date">;

/**
 * List/detail primary label: user title when set, else `type vN · window` for legacy rows.
 */
export function getReportDisplayTitle(report: ReportTitleFields): string {
  const custom = (report.title ?? "").trim();
  if (custom) return custom;
  const windowPart =
    formatReportWindowLabelForDisplay(report, { whenUnset: "all-time" }) ?? "All time";
  const typePart = getReportTypeDisplayLabel(report.report_type);
  const ver = report.report_version ?? 1;
  return `${typePart} v${ver} · ${windowPart}`;
}
